import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PostCard from './PostCard';
import * as LucideIcons from 'lucide-react';
import CommentModal from './CommentModal';
import ParticleBurst from './ParticleBurst';
import AiToolsButton from './AiToolsButton';
import AmplifyModal from './AmplifyModal';
import RealtimeCommentCount from './RealtimeCommentCount';
import { useAppContext } from '../AppContext';
import { useTranslation } from 'react-i18next';
import AnimatedCount from './AnimatedCount';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

const CardActionBar = React.memo(({ 
    post, 
    userId, 
    handleLikeToggle, 
    handleDislikeToggle, 
    handleDelete, 
    setShowAmplifyModal, 
    handleResonate,
    onShowComments, 
    optimisticLikes, 
    optimisticLikesCount, 
    optimisticDislikes, 
    optimisticDislikesCount, 
    optimisticEchoes, 
    optimisticResonates,
    currentUserProfile, 
    handleAiAnalysis, 
    onShareToVision, 
    onReport 
}) => {
    const { showConfirmation } = useAppContext();
    const [isLiked, setIsLiked] = useState(false);
    const { t } = useTranslation();

    const handleLikeClick = () => {
        setIsLiked(true);
        handleLikeToggle(post.id, post.authorId);
        setTimeout(() => setIsLiked(false), 300);
    };

    return (
        <div className="absolute bottom-4 right-3 sm:right-6 flex flex-col items-center gap-2 sm:gap-4 z-20 pointer-events-none">
            <div className="flex flex-col items-center pointer-events-auto">
                <button className="text-white transition-transform hover:scale-110 active:scale-95 p-1.5 sm:p-2" onClick={() => onReport(post)}>
                    <LucideIcons.Flag size={28} className="sm:w-8 sm:h-8" />
                </button>
            </div>
            <div className="flex flex-col items-center pointer-events-auto">
                <button className="text-white transition-transform hover:scale-110 active:scale-95 p-1.5 sm:p-2" onClick={handleLikeClick}>
                    <div className={isLiked ? 'like-pop-animation' : ''}>
                        <LucideIcons.Heart size={28} className="sm:w-8 sm:h-8" fill={optimisticLikes.includes(userId) ? '#ef4444' : 'transparent'} />
                    </div>
                </button>
                <span className="text-[10px] sm:text-xs text-white drop-shadow-md"><AnimatedCount value={optimisticLikesCount || 0} /></span>
            </div>
            <div className="flex flex-col items-center pointer-events-auto">
                <button className="text-white transition-transform hover:scale-110 active:scale-95 p-1.5 sm:p-2" onClick={() => handleDislikeToggle(post.id, post.authorId)}>
                    <LucideIcons.ThumbsDown size={28} className="sm:w-8 sm:h-8" fill={optimisticDislikes.includes(userId) ? '#fff' : 'transparent'} />
                </button>
                <span className="text-[10px] sm:text-xs text-white drop-shadow-md"><AnimatedCount value={optimisticDislikesCount || 0} /></span>
            </div>
            <div className="flex flex-col items-center pointer-events-auto">
                <button className="text-white transition-transform hover:scale-110 active:scale-95 p-1.5 sm:p-2" onClick={() => setShowAmplifyModal(true)}>
                    <LucideIcons.Flame size={28} className="sm:w-8 sm:h-8" />
                </button>
                <span className="text-[10px] sm:text-xs text-white drop-shadow-md">{optimisticEchoes}</span>
            </div>
            <div className="flex flex-col items-center pointer-events-auto">
                <button className="text-white transition-transform hover:scale-110 active:scale-95 p-1.5 sm:p-2" onClick={() => handleResonate(post.id)}>
                    <LucideIcons.Zap size={28} className="sm:w-8 sm:h-8" />
                </button>
                <span className="text-[10px] sm:text-xs text-white drop-shadow-md"><AnimatedCount value={optimisticResonates || 0} /></span>
            </div>
            <div className="flex flex-col items-center pointer-events-auto">
                <button className="text-white transition-transform hover:scale-110 active:scale-95 p-1.5 sm:p-2" onClick={() => onShowComments(post)}>
                    <LucideIcons.MessageSquareReply size={28} className="sm:w-8 sm:h-8" />
                </button>
                <RealtimeCommentCount postId={post.id} contentType={post.contentType} nexusId={post.nexusId} />
            </div>
            <div className="pointer-events-auto -mt-2 -translate-x-2">
                <AiToolsButton 
                    content={post.content} 
                    entryId={post.id} 
                    authorId={post.authorId}
                    contextType="whisper"
                    onAnalysisComplete={handleAiAnalysis}
                    onShareToVision={onShareToVision}
                    onDelete={() => showConfirmation({ message: t('reelPost.deleteConfirmation'), onConfirm: () => handleDelete(post.id) })}
                    canDelete={userId === post.authorId || currentUserProfile?.role === 'admin' || currentUserProfile?.role === 'owner'}
                />
            </div>
        </div>
    );
});

const ReelPost = ({ post, onShareToVision, ...props }) => {
    const { setMessage, db, appId, appFunctions, userProfiles: contextUserProfiles } = useAppContext();
    const { t } = useTranslation();
    const [showAmplifyModal, setShowAmplifyModal] = useState(false);
    const [showParticleBurst, setShowParticleBurst] = useState(false);
    
    const activeProfiles = props.userProfiles || contextUserProfiles || [];
    const authorProfile = useMemo(() => activeProfiles.find(p => p.id === post.authorId), [activeProfiles, post.authorId]);

    const [optimisticLikes, setOptimisticLikes] = useState(post.likes || []);
    const [optimisticLikesCount, setOptimisticLikesCount] = useState(post.likesCount || 0);
    const [optimisticDislikes, setOptimisticDislikes] = useState(post.dislikes || []);
    const [optimisticDislikesCount, setOptimisticDislikesCount] = useState(post.dislikesCount || 0);
    const [amplifyAmount, setAmplifyAmount] = useState(0);
    const [resonateAmount, setResonateAmount] = useState(0);
    const [livePostData, setLivePostData] = useState(post);

    // Sync with Real-time DB
    useEffect(() => {
        const postRef = doc(db, `artifacts/${appId}/public/data/whispers`, post.id);
        const unsubscribe = onSnapshot(postRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setLivePostData(prev => ({ ...prev, ...data }));
                setOptimisticLikes(data.likes || []);
                setOptimisticLikesCount(data.likesCount || 0);
                setOptimisticDislikes(data.dislikes || []);
                setOptimisticDislikesCount(data.dislikesCount || 0);
                setAmplifyAmount(0);
                setResonateAmount(0);
            }
        }, (error) => {
            console.error("Error listening to post updates:", error);
        });
        return () => unsubscribe();
    }, [db, appId, post.id]);

    const handleOptimisticAmplify = (amount) => {
        setAmplifyAmount(prev => prev + amount);
        setShowParticleBurst(true);
    };

    const handleLikeToggle = useCallback(async (entryId, authorId) => {
        const isCurrentlyLiked = optimisticLikes.includes(props.userId);
        
        if (isCurrentlyLiked) {
            setOptimisticLikes(prev => prev.filter(id => id !== props.userId));
            setOptimisticLikesCount(prev => prev - 1);
        } else {
            setOptimisticLikes(prev => [...prev, props.userId]);
            setOptimisticLikesCount(prev => prev + 1);
        }

        if (optimisticDislikes.includes(props.userId)) {
            setOptimisticDislikes(prev => prev.filter(id => id !== props.userId));
            setOptimisticDislikesCount(prev => prev - 1);
        }

        try {
            await props.handleLikeToggle(entryId, authorId);
        } catch (error) {
            console.error("Failed to toggle like, reverting UI.", error);
            setMessage(error.message);
            // Revert is tricky if DB listener fires in between. 
            // Usually DB listener is source of truth. Optimistic is just visual filler.
        }
    }, [optimisticLikes, optimisticDislikes, props.userId, props.handleLikeToggle, setMessage]);

    const handleAmplify = (entryId) => {
        setShowParticleBurst(true);
        props.handleAmplify(entryId);
    };

    const handleResonateAction = async (entryId) => {
        setShowParticleBurst(true);
        setResonateAmount(prev => prev + 5);
        try {
            const resonateFn = httpsCallable(appFunctions, 'resonateWithPost');
            await resonateFn({ whisperId: entryId, amount: 5 });
            setMessage("Successfully resonated!");
        } catch (error) {
            console.error("Failed to resonate:", error);
            setMessage(error.message);
            setResonateAmount(prev => prev - 5);
        }
    };

    const handleDislikeToggle = useCallback(async (entryId, authorId) => {
        const isCurrentlyDisliked = optimisticDislikes.includes(props.userId);

        if (isCurrentlyDisliked) {
            setOptimisticDislikes(prev => prev.filter(id => id !== props.userId));
            setOptimisticDislikesCount(prev => prev - 1);
        } else {
            setOptimisticDislikes(prev => [...prev, props.userId]);
            setOptimisticDislikesCount(prev => prev + 1);
        }

        if (optimisticLikes.includes(props.userId)) {
            setOptimisticLikes(prev => prev.filter(id => id !== props.userId));
            setOptimisticLikesCount(prev => prev - 1);
        }

        try {
            await props.handleDislikeToggle(entryId, authorId);
        } catch (error) {
            console.error("Failed to toggle dislike, reverting UI.", error);
            setMessage(error.message); 
        }
    }, [optimisticDislikes, optimisticLikes, props.userId, props.handleDislikeToggle, setMessage]);

    return (
        <>
            {showParticleBurst && <ParticleBurst onComplete={() => setShowParticleBurst(false)} />}
            {showAmplifyModal && <AmplifyModal whisper={post} onClose={() => setShowAmplifyModal(false)} onAmplify={handleOptimisticAmplify} />}
            <div className="reel-item reel-entry-animation">
                <PostCard post={livePostData} {...props} className="relative z-10 pr-14 sm:pr-20" onShareToVision={onShareToVision} contentType={post.contentType} />
                <CardActionBar 
                    post={livePostData}
                    userId={props.userId}
                    handleLikeToggle={handleLikeToggle}
                    handleDislikeToggle={handleDislikeToggle}
                    handleDelete={props.handleDelete}
                    setShowAmplifyModal={setShowAmplifyModal}
                    handleResonate={handleResonateAction}
                    onShowComments={props.onShowComments}
                    optimisticLikes={optimisticLikes}
                    optimisticLikesCount={optimisticLikesCount}
                    optimisticDislikes={optimisticDislikes}
                    optimisticDislikesCount={optimisticDislikesCount}
                    optimisticEchoes={(livePostData.echoesInvested || 0) + amplifyAmount}
                    optimisticResonates={(livePostData.resonates || 0) + resonateAmount}
                    currentUserProfile={props.currentUserProfile}
                    handleAiAnalysis={props.handleAiAnalysis}
                    onShareToVision={onShareToVision}
                    onReport={props.onReport}
                />
            </div>
        </>
    );
};

export default ReelPost;